/**
 * The Unplaced tray (FD §3.2, §3.3): animal groups that aren't on the map yet, kept in the plan (doc.unplaced)
 * so everyone who opens it sees the same tray. Drag one onto the map to place it at its needed size, or let the
 * planner "make room" by re-arranging the habitats. Every change is an undoable edit.
 */
import { useMemo } from 'react';
import { useEditor, type EditorStore } from '../../../store/editor';
import { makeRoom, neededArea } from '../../../tools/edits';
import { KIND_ICON } from '../../../render/palette';

/** Drag-and-drop type of a tray entry. */
export const TRAY_MIME = 'application/x-zoo-unplaced';

const fmt = (n: number) => Math.round(n).toLocaleString('en-US');

/** The tray panel; hidden when empty. */
export function UnplacedTray({ store }: { store: EditorStore }) {
  const tray = useEditor(store, (s) => s.doc.unplaced);
  const species = useEditor(store, (s) => s.species);
  useEditor(store, (s) => s.doc.settings.extraSpacePct);
  const items = useMemo(() => Object.entries(tray).map(([id, u]) => ({ id, ...u })), [tray]);
  if (!items.length) return null;
  return (
    <section className="panel">
      <h3>Unplaced ({items.length})</h3>
      <p className="hint small">Drag a habitat onto the map, or press Make room to fit it in by re-arranging.</p>
      <ul className="tray">
        {items.map((it) => {
          const sp = species?.find((s) => s.id === it.speciesId);
          const icon = sp ? KIND_ICON[sp.kind] : undefined;
          return (
            <li
              key={it.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData(TRAY_MIME, JSON.stringify(it));
                e.dataTransfer.effectAllowed = 'copy';
              }}
              title={`Needs about ${fmt(neededArea(store, it.speciesId, it.count))} m²`}
            >
              <span aria-hidden="true">{icon ?? '🐾'}</span>
              <span className="grow">{sp?.name ?? 'Unknown animal'} ×{it.count}</span>
              <button type="button" onClick={() => makeRoom(store, it.speciesId, it.count, it.id)} disabled={!species}>Make room</button>
              <button type="button" className="quiet" title="Remove from the list" aria-label="Remove from the list" onClick={() => store.removeFromTray(it.id)}>✕</button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
